import { Injectable } from '@angular/core';
import { Observable, of, switchMap } from 'rxjs';
import { CmsContentTypes } from '../interfaces/cms';

import { ContentfulGetterService } from './contentful-getter.service';
import { AcademyHelper } from '../utils/helpers/academy.helper';
import {
  CmsAcademyVideosDurations,
  CmsAcademyVideosSortingModes,
  IAcademyVideosFilters,
  IAcademyVideosItem,
  ICmsAcademyVideo
} from '../interfaces/academy-video';
import {
  IAcademyBooksFilters,
  IAcademyBookItem,
  CmsAcademyBooksSortingModes
} from '../interfaces/academy-library';
import { Options } from '@contentful/rich-text-html-renderer';
import {
  IAcademyOfferItem,
  IAcademyOffersResponseFormatted
} from '../interfaces/academy-offers';

@Injectable(
  { providedIn: 'root' }
)
export class AcademyContentfulService {
  constructor(private contentfulGetterService: ContentfulGetterService) {}

  renderOptionsNewLine: Options = {
    renderNode: {
      ['paragraph']: (node, next) =>
        `<p>${next(node.content).replace(/\n/g, `</br>`)}</p>`
    }
  };

  getAcademyBooks$(
    filters?: IAcademyBooksFilters,
    limit?: number,
    skip = 0
  ): Observable<{ books: IAcademyBookItem[]; total: number }> {
    const query = {
      content_type: CmsContentTypes.AcademyBookItem,
      limit,
      skip
    };

    switch (filters?.sortingMode) {
      case CmsAcademyBooksSortingModes.PublishDate:
        Object.assign(query, {
          order: '-fields.publishDate'
        });
        break;
      case CmsAcademyBooksSortingModes.NewestFirst:
        Object.assign(query, {
          order: '-fields.dateCreated'
        });
        break;
      case CmsAcademyBooksSortingModes.Title:
        Object.assign(query, {
          order: 'fields.title'
        });
        break;

      default:
        Object.assign(query, {
          order: '-fields.dateCreated'
        });
    }

    if (filters?.categories?.length) {
      Object.assign(query, {
        'fields.categories[in]': filters?.categories
      });
    }

    if (filters?.languages?.length) {
      Object.assign(query, {
        'fields.language[in]': filters?.languages
      });
    }

    if (filters?.genre) {
      Object.assign(query, {
        'fields.genre': filters?.genre
      });
    }

    return this.contentfulGetterService.getEntries<
      IAcademyBookItem,
      { books: IAcademyBookItem[]; total: number }
    >(
      false,
      CmsContentTypes.AcademyBookItem,
      AcademyHelper.getAcademyBookListHandler,
      query
    );
  }

  getTitlesOfTheMonthBooks$(): Observable<IAcademyBookItem[]> {
    const query = {
      content_type: CmsContentTypes.AcademyBookItem,
      'fields.titleOfTheMonth': true,
      order: '-fields.dateCreated',
      limit: 4
    };

    return this.contentfulGetterService
      .getEntries<
        IAcademyBookItem,
        { books: IAcademyBookItem[]; total: number }
      >(
        false,
        CmsContentTypes.AcademyBookItem,
        AcademyHelper.getAcademyBookListHandler,
        query
      )
      .pipe(
        switchMap((bookListResponse) => {
          return of(bookListResponse.books);
        })
      );
  }

  getAcademyBookItemById$(
    bookId: string,
    previewMode = false
  ): Observable<IAcademyBookItem> {
    const query = {
      content_type: CmsContentTypes.AcademyBookItem,
      'sys.id': bookId,
      limit: 1
    };

    return this.contentfulGetterService
      .getEntries<IAcademyBookItem, IAcademyBookItem>(
        previewMode,
        CmsContentTypes.AcademyBookItem,
        AcademyHelper.getAcademyBookItemHandler,
        query,
        this.renderOptionsNewLine
      )
      .pipe(
        switchMap((result: IAcademyBookItem) => {
          return of(result || null);
        })
      );
  }

  getAcademyVideoItems$(
    filters?: IAcademyVideosFilters,
    limit?: number,
    skip = 0
  ): Observable<{ videos: Array<IAcademyVideosItem>; total: number }> {
    const query = { content_type: CmsContentTypes.AcademyVideo, limit, skip };

    switch (filters?.sortingMode) {
      case CmsAcademyVideosSortingModes.Date:
        Object.assign(query, {
          order: '-fields.videoDate'
        });
        break;
      case CmsAcademyVideosSortingModes.Duration:
        Object.assign(query, {
          order: 'fields.duration'
        });
        break;
      case CmsAcademyVideosSortingModes.Title:
        Object.assign(query, {
          order: 'fields.title'
        });
        break;

      default:
        Object.assign(query, {
          order: '-fields.videoDate'
        });
    }

    if (filters?.categories) {
      Object.assign(query, {
        'fields.categories[in]': filters?.categories
      });
    }

    switch (filters?.duration) {
      case CmsAcademyVideosDurations.Short:
        Object.assign(query, {
          'fields.duration[lt]': 10
        });
        break;
      case CmsAcademyVideosDurations.Medium:
        Object.assign(query, {
          'fields.duration[gte]': 10
        });
        Object.assign(query, {
          'fields.duration[lte]': 60
        });
        break;
      case CmsAcademyVideosDurations.Long:
        Object.assign(query, {
          'fields.duration[gt]': 60
        });
        break;
    }

    if (filters?.languages) {
      Object.assign(query, {
        'fields.videoLanguages[in]': filters?.languages
      });
    }

    return this.contentfulGetterService.getEntries<
      ICmsAcademyVideo,
      { videos: Array<IAcademyVideosItem>; total: number }
    >(
      false,
      CmsContentTypes.AcademyVideo,
      AcademyHelper.getAcademyVideosHandler,
      query
    );
  }

  getFeaturedAcademyVideoItem$(): Observable<IAcademyVideosItem> {
    const query = {
      content_type: CmsContentTypes.AcademyVideo,
      'fields.isFeatured': true,
      limit: 1,
      order: '-fields.videoDate'
    };

    return this.contentfulGetterService
      .getEntries<
        ICmsAcademyVideo,
        { videos: Array<IAcademyVideosItem>; total: number }
      >(
        false,
        CmsContentTypes.AcademyVideo,
        AcademyHelper.getAcademyVideosHandler,
        query
      )
      .pipe(
        switchMap(
          (results: { videos: Array<IAcademyVideosItem>; total: number }) => {
            return of(results.videos[0] || null);
          }
        )
      );
  }

  getAcademyVideoItem$(
    videoId: string,
    previewMode = false
  ): Observable<IAcademyVideosItem> {
    const query = {
      content_type: CmsContentTypes.AcademyVideo,
      'sys.id': videoId,
      limit: 1
    };

    return this.contentfulGetterService
      .getEntries<
        ICmsAcademyVideo,
        { videos: Array<IAcademyVideosItem>; total: number }
      >(
        previewMode,
        CmsContentTypes.AcademyVideo,
        AcademyHelper.getAcademyVideosHandler,
        query,
        this.renderOptionsNewLine
      )
      .pipe(
        switchMap(
          (results: { videos: Array<IAcademyVideosItem>; total: number }) => {
            return of(results.videos[0] || null);
          }
        )
      );
  }

  getAcademyOffers$(): Observable<IAcademyOffersResponseFormatted> {
    const query = {
      content_type: CmsContentTypes.AcademyOfferItem,
      order: '-fields.dateCreated'
    };

    return this.contentfulGetterService.getEntries<
      IAcademyOfferItem,
      IAcademyOffersResponseFormatted
    >(
      false,
      CmsContentTypes.AcademyOfferItem,
      AcademyHelper.getAcademyOffersHandler,
      query
    );
  }

  getAcademyOfferItem$(
    offerId: string,
    previewMode = false
  ): Observable<IAcademyOfferItem> {
    const query = {
      content_type: CmsContentTypes.AcademyOfferItem,
      'sys.id': offerId,
      limit: 1
    };

    return this.contentfulGetterService
      .getEntries<IAcademyOfferItem, IAcademyOfferItem>(
        previewMode,
        CmsContentTypes.AcademyOfferItem,
        AcademyHelper.getAcademyOfferHandler,
        query,
        this.renderOptionsNewLine
      )
      .pipe(
        switchMap((result: IAcademyOfferItem) => {
          return of(result || null);
        })
      );
  }
}
